import { v4 as uuidv4 } from 'uuid'
import type { Timestamp } from '../firebase.ts'
import type { UserId } from '../ids.ts'
import type { StenResultsType } from '../Sten.ts'
import { SurveyTypes } from '../Survey.ts'

export type SelfKnowledgeSurveyId = string

export interface SelfKnowledgeSurvey {
    id: SelfKnowledgeSurveyId
    surveyType: SurveyTypes
    userId: UserId
    hash?: string
    questions: SelfKnowledgeSurveyQuestionTypes[]
    stens: StenResultsType
    fit: StenResultsType
    totalFit?: number
    createdAt: Timestamp
}

export enum SurveyFactorTypes {
    PERFORMANCE = 'performance',
    CONSCIENTIOUSNESS = 'conscientiousness',
    TEAMWORK = 'teamwork',
    EMO = 'emo',
    SELFREFLECTION = 'selfreflection',
    DIS = 'dis',
    HEX = 'hex',
    MORALITY = 'morality',
    LIE = 'lie',
    HELP_SLOVAKIA = 'help_slovakia' // only because of merge these two surveys, wh have to mark these questions
}

export enum ImpactWeight {
    HIGH,
    MIDDLE,
    INVERT_MIDDLE,
    INVERT_HIGH,
    MIDDLE_HIGH,
    LOW,
    ZERO
}

export const impactConfiguration = {
    [SurveyFactorTypes.PERFORMANCE]: ImpactWeight.HIGH,
    [SurveyFactorTypes.CONSCIENTIOUSNESS]: ImpactWeight.HIGH,
    [SurveyFactorTypes.TEAMWORK]: ImpactWeight.MIDDLE,
    [SurveyFactorTypes.MORALITY]: ImpactWeight.HIGH,
    [SurveyFactorTypes.SELFREFLECTION]: ImpactWeight.HIGH,
    [SurveyFactorTypes.EMO]: ImpactWeight.HIGH,
    [SurveyFactorTypes.DIS]: ImpactWeight.INVERT_MIDDLE,
    [SurveyFactorTypes.HEX]: ImpactWeight.INVERT_HIGH,
    [SurveyFactorTypes.LIE]: ImpactWeight.ZERO,
    [SurveyFactorTypes.HELP_SLOVAKIA]: ImpactWeight.ZERO
}

export const impactTransformation = {

    //                                 0   1   2   3   4   5   6   7   8   9  10
    [ImpactWeight.HIGH]:            [ -3, -3, -2, -2, -1,  0,  1,  2,  2,  3,  3 ], // VYSOKY
    [ImpactWeight.MIDDLE]:          [ -2, -1,  0,  1,  1,  2,  2,  2,  3,  3,  3 ], // STREDNY
    [ImpactWeight.INVERT_MIDDLE]:   [  3,  3,  2,  2,  1,  1,  0, -1, -2, -2, -2 ], // INVERT-STŘED
    [ImpactWeight.INVERT_HIGH]:     [  3,  3,  2,  2,  1,  0, -1, -2, -2, -3, -3 ], // INVERT-VYSOKÝ
    [ImpactWeight.MIDDLE_HIGH]:     [ -3, -2, -1, -1,  0,  1,  2,  2,  2,  3,  3 ], // STŘEDNĚ VYSOKÝ
    [ImpactWeight.LOW]:             [ -1, -1,  0,  1,  2,  2,  2,  2,  3,  3,  3 ], // NÍZKÝ
    [ImpactWeight.ZERO]:            [  0,  0,  0,  0,  0,  0,  0,  0,  0,  0,  0 ]  // NULOVÝ

}

export const OrderedSurveyFactors = [
    SurveyFactorTypes.PERFORMANCE,
    SurveyFactorTypes.CONSCIENTIOUSNESS,
    SurveyFactorTypes.TEAMWORK,
    SurveyFactorTypes.EMO,
    SurveyFactorTypes.SELFREFLECTION,
    SurveyFactorTypes.DIS,
    SurveyFactorTypes.HEX,
    SurveyFactorTypes.MORALITY,
    SurveyFactorTypes.LIE,
    SurveyFactorTypes.HELP_SLOVAKIA
]

export const stenParams = {
    [SurveyFactorTypes.PERFORMANCE]: {
        ssPercents: 60.0,
        miValue: 23.632,
        roValue: 4.326
    },
    [SurveyFactorTypes.CONSCIENTIOUSNESS]: {
        ssPercents: 55.0,
        miValue: 20.450,
        roValue: 4.365
    },
    [SurveyFactorTypes.TEAMWORK]: {
        ssPercents: 62.0,
        miValue: 24.637,
        roValue: 5.709
    },
    [SurveyFactorTypes.EMO]: {
        ssPercents: 71.11,
        miValue: 23.614,
        roValue: 5.695
    },
    [SurveyFactorTypes.SELFREFLECTION]: {
        ssPercents: 70.0,
        miValue: 21.783,
        roValue: 5.486
    },
    [SurveyFactorTypes.DIS]: {
        ssPercents: 46.0,
        miValue: 20.149,
        roValue: 6.611
    },
    [SurveyFactorTypes.HEX]: {
        ssPercents: 41.67,
        miValue: 23.021,
        roValue: 7.085
    },
    [SurveyFactorTypes.MORALITY]: {
        ssPercents: 77.5,
        miValue: 23.247,
        roValue: 5.698
    },
    [SurveyFactorTypes.LIE]: {
        ssPercents: undefined,
        miValue: undefined,
        roValue: undefined
    },
    [SurveyFactorTypes.HELP_SLOVAKIA]: {
        ssPercents: undefined,
        miValue: undefined,
        roValue: undefined
    }
}

export type BigSurveyFactorTypes = (typeof SurveyFactorTypes)[keyof typeof SurveyFactorTypes]

export enum SurveyQuestionTypes {
    CHOICES = 'CSurveyChoice',
    SCALE = 'CSurveyScale',
    TRUEFALSE = 'CSurveyTrueFalse',
    INPUT = 'CSurveyInput'
}

export interface SelfKnowledgeSurveyQuestionTypes {
    id: string
    factors: BigSurveyFactorTypes
    type: SurveyQuestionTypes
    weight?: number
    reversed?: boolean
    title: string
    options?: object[]
    value?: number
    transposed?: number
    unaccounted?: boolean
}

export const initStenResults: StenResultsType = {
    [SurveyFactorTypes.PERFORMANCE]: 0,
    [SurveyFactorTypes.CONSCIENTIOUSNESS]: 0,
    [SurveyFactorTypes.TEAMWORK]: 0,
    [SurveyFactorTypes.EMO]: 0,
    [SurveyFactorTypes.SELFREFLECTION]: 0,
    [SurveyFactorTypes.DIS]: 0,
    [SurveyFactorTypes.HEX]: 0,
    [SurveyFactorTypes.MORALITY]: 0,
    [SurveyFactorTypes.LIE]: 0,
    [SurveyFactorTypes.HELP_SLOVAKIA]: 0
}

export const SelfKnowledgeSurveyQuestions: SelfKnowledgeSurveyQuestionTypes[] = [
    {
        id: '1',
        factors: SurveyFactorTypes.PERFORMANCE,
        type: SurveyQuestionTypes.SCALE,
        title: 'Rád/a sa púšťam do nových vecí/aktivít, mením a inovujem.'
    },
    {
        id: '11',
        factors: SurveyFactorTypes.SELFREFLECTION,
        type: SurveyQuestionTypes.SCALE,
        title: 'Často rozoberám v sebe svoje myšlienky a motivácie.'
    },
    // Old "slepe crevo"
    // {
    //     id: '21',
    //     unaccounted: true,
    //     factors: SurveyFactorTypes.CONSCIENTIOUSNESS,
    //     type: SurveyQuestionTypes.SCALE,
    //     reversed: true,
    //     title: 'Nerád/a vyhľadávam rady a pomoc, buď mi niečo ide alebo nie.'
    // },

    // Question no. 2 from Chuť pomôcť Slovensku
    {
        id: '21',
        unaccounted: true,
        factors: SurveyFactorTypes.HELP_SLOVAKIA,
        type: SurveyQuestionTypes.SCALE,
        title: 'Osobne poznám ľudí, ktorí pracujú na rozvoji Slovenska.'
    },
    {
        id: '31',
        factors: SurveyFactorTypes.DIS,
        type: SurveyQuestionTypes.SCALE,
        title: 'Všetko treba vyskúšať.'
    },
    {
        id: '41',
        factors: SurveyFactorTypes.EMO,
        type: SurveyQuestionTypes.CHOICES,
        title: 'Keď sa ku mne niekto zle zachová a rozruší ma to (nahnevá, zarmúti, atď.)...',
        options: [
            {
                value: 6,
                text: '...premýšľam, čím to je, že to na mňa tak zapôsobilo.'
            },
            {
                value: 4,
                text: '...premýšľam, s čím konkrétne má ten človek problém.'
            },
            {
                value: 2,
                text: '...premýšľam, prečo má ten človek problém práve so mnou.'
            },
            {
                value: 1,
                text: '...premýšľam nad tým, ako môžu byť niektorí ľudia takí nepríjemní.'
            }
        ]
    },
    {
        id: '51',
        factors: SurveyFactorTypes.HEX,
        type: SurveyQuestionTypes.SCALE,
        title: 'Niekedy lichotím nadriadeným, ktorých nemám rád/a, lebo viem, že je dôležité s nimi dobre vychádzať.'
    },
    {
        id: '61',
        factors: SurveyFactorTypes.TEAMWORK,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Nerád/a sa prispôsobujem potrebám a predstavám druhých ľudí.'
    },
    {
        // "súhlasím";1;0
        id: 'L71',
        factors: SurveyFactorTypes.LIE,
        type: SurveyQuestionTypes.TRUEFALSE,
        weight: 1,
        title: 'Ak sľúbim, že niečo urobím, dodržím slovo vždy, bez ohľadu na to, čo do toho príde.'
    },
    {
        id: '81',
        factors: SurveyFactorTypes.MORALITY,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'V práci vždy splním, čo sa odo mňa očakáva, aj keď je to v rozpore s mojím presvedčením.'
    },
    {
        id: '2',
        factors: SurveyFactorTypes.PERFORMANCE,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Keď niečo funguje, nevidím dôvod to meniť (zlepšovať).'
    },
    {
        id: '12',
        factors: SurveyFactorTypes.SELFREFLECTION,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Nemá zmysel zaoberať sa neúspechmi, potrebné je hľadieť dopredu.'
    },
    {
        id: '22',
        factors: SurveyFactorTypes.CONSCIENTIOUSNESS,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Je dôležité nemrhať príliš veľa času na veci, ktoré mi zjavne nejdú.'
    },
    {
        id: '32',
        factors: SurveyFactorTypes.DIS,
        type: SurveyQuestionTypes.SCALE,
        title: 'Mám rád/a okolo seba ľudí, ktorí idú proti prúdu.'
    },
    // Old "slepe crevo"
    // {
    //     id: '42',
    //     unaccounted: true,
    //     factors: SurveyFactorTypes.EMO,
    //     type: SurveyQuestionTypes.CHOICES,
    //     title: 'Keď mám toho veľmi veľa a cítim stres...',
    //     options: [
    //         {
    //             value: 6,
    //             text: '...cítim sa zle len chvíľu, ale rýchlo to prejde.'
    //         },
    //         {
    //             value: 4,
    //             text: '...cítim sa zle, ale hľadám spôsoby, ako to zlepšiť.'
    //         },
    //         {
    //             value: 2,
    //             text: '...cítim sa zle, ale viem, ako sa toho stresu zbaviť.'
    //         },
    //         {
    //             value: 1,
    //             text: '...cítim sa zle, ale nedá sa s tým skoro nič urobiť.'
    //         }
    //     ]
    // },

    // Question no. 5 from Chuť pomôcť Slovensku
    {
        id: '42',
        unaccounted: true,
        factors: SurveyFactorTypes.HELP_SLOVAKIA,
        type: SurveyQuestionTypes.SCALE,
        title: 'Pravidelne prispievam na charitu alebo iné projekty pomoci.'
    },
    {
        id: '52',
        factors: SurveyFactorTypes.HEX,
        type: SurveyQuestionTypes.SCALE,
        title: 'Keď viem, že budem od niekoho niečo potrebovať, som k nemu milý/á, aj keď mi ľudsky úplne nesedí.'
    },
    {
        id: '62',
        factors: SurveyFactorTypes.TEAMWORK,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Samostatne toho vyriešime viac, než pri práci v tíme.'
    },
    {
        // "súhlasím";1;0
        id: 'L72',
        factors: SurveyFactorTypes.LIE,
        type: SurveyQuestionTypes.TRUEFALSE,
        weight: 1,
        title: 'Vždy sa riadim presne tým, čo hovorím.'
    },
    {
        id: '82',
        factors: SurveyFactorTypes.MORALITY,
        type: SurveyQuestionTypes.SCALE,
        title: 'Veľmi často premýšľam nad etickou stránkou mojich rozhodnutí.'
    },
    {
        id: '3',
        factors: SurveyFactorTypes.PERFORMANCE,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Pri spolupráci s ostatnými je pre mňa dôležitejšie to, aby sme si rozumeli, než to, aké výsledky budeme mať.'
    },
    {
        id: '13',
        factors: SurveyFactorTypes.SELFREFLECTION,
        type: SurveyQuestionTypes.SCALE,
        title: 'Keď v niečom zlyhám, dlho premýšľam nad tým, čo som mohol/mohla urobiť inak.'
    },
    {
        id: '23',
        factors: SurveyFactorTypes.CONSCIENTIOUSNESS,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Radšej si veci neplánujem, lebo život sa mení príliš rýchlo.'
    },
    {
        id: '33',
        factors: SurveyFactorTypes.DIS,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Väčšinou mi dlho trvá, kým sa s niekým zblížim.'
    },
    {
        id: '43',
        factors: SurveyFactorTypes.EMO,
        type: SurveyQuestionTypes.CHOICES,
        title: 'Keď sa môj blízky cíti ťažko (smutný/á, nahnevaný/á, v strese)...',
        options: [
            {
                value: 6,
                text: '...často ho/ju viem upokojiť.'
            },
            {
                value: 4,
                text: '...často ho/ju neviem upokojiť, ale chcel/a by som sa to naučiť.'
            },
            {
                value: 2,
                text: '...neviem ho/ju upokojiť, aj keď to skúšam.'
            },
            {
                value: 1,
                text: '...nechám ho/ju tak, každý je zodpovedný za vlastné emócie.'
            }
        ]
    },
    {
        id: '53',
        factors: SurveyFactorTypes.HEX,
        type: SurveyQuestionTypes.SCALE,
        title: 'Keď prídem do novej skupiny/tímu, vždy sa najskôr zoznámim s najdôležitejšími ľuďmi.'
    },
    {
        id: '63',
        factors: SurveyFactorTypes.TEAMWORK,
        type: SurveyQuestionTypes.SCALE,
        title: 'V tíme som obvykle kreatívnejší/kreatívnejšia, než pri samostatnej práci.'
    },
    {
        // "súhlasím";1;0
        id: 'L73',
        factors: SurveyFactorTypes.LIE,
        type: SurveyQuestionTypes.TRUEFALSE,
        weight: 1,
        title: 'Keď veci majú byť urobené dnes, neodkladám ich nikdy na zajtra.'
    },
    {
        // "súhlasím";3;0)
        id: '83',
        factors: SurveyFactorTypes.MORALITY,
        type: SurveyQuestionTypes.TRUEFALSE,
        weight: 3,
        title: 'Viem uviesť príklad morálnej dilemy, ktorú som riešil/a za posledný rok.'
    },
    {
        id: '4',
        factors: SurveyFactorTypes.PERFORMANCE,
        type: SurveyQuestionTypes.SCALE,
        title: 'Rád/a sa obklopujem ľuďmi, ktorí sú o niečo lepší než ja.'
    },
    // Old "slepe crevo"
    // {
    //     id: '14',
    //     unaccounted: true,
    //     factors: SurveyFactorTypes.SELFREFLECTION,
    //     type: SurveyQuestionTypes.SCALE,
    //     reversed: true,
    //     title: 'Je pre mňa niekedy ťažké premýšľať o svojich silných stránkach a nedostatkoch.'
    // },
    // Question no. 6 from Chuť pomôcť Slovensku
    {
        id: '14',
        unaccounted: true,
        factors: SurveyFactorTypes.HELP_SLOVAKIA,
        reversed: true,
        type: SurveyQuestionTypes.SCALE,
        title: 'Zmena v spoločnosti je v rukách politikov, bežný človek s tým nič nespraví.'
    },

    // Old "slepe crevo"
    // {
    //     id: '24',
    //     unaccounted: true,
    //     factors: SurveyFactorTypes.CONSCIENTIOUSNESS,
    //     type: SurveyQuestionTypes.CHOICES,
    //     title: 'Keď veci nejdú podľa plánu...',
    //     options: [
    //         {
    //             value: 6,
    //             text: '...hneď mi napadne viacero spôsobov, ako by sa to dalo robiť.'
    //         },
    //         {
    //             value: 4,
    //             text: '...najprv vôbec neviem čo mám robiť, ale neskôr niečo vymyslím.'
    //         },
    //         {
    //             value: 2,
    //             text: '...je dobré nechať to tak, asi to tak malo byť.'
    //         },
    //         {
    //             value: 1,
    //             text: '...väčšinou ma to nepríjemne zaskočí a nechcem s tým už nič mať.'
    //         }
    //     ]
    // },

    // Question no. 3 from Chuť pomôcť Slovensku
    {
        id: '24',
        unaccounted: true,
        factors: SurveyFactorTypes.HELP_SLOVAKIA,
        type: SurveyQuestionTypes.SCALE,
        title: 'Pokiaľ som vedel/a niekomu pomôcť, spravil/a som to bez nároku na to, aby mi to vrátil/a.'
    },
    {
        id: '34',
        factors: SurveyFactorTypes.DIS,
        type: SurveyQuestionTypes.SCALE,
        title: 'Do nových vzťahov idem impulzívne, bez rozmýšľania.'
    },
    {
        id: '44',
        factors: SurveyFactorTypes.EMO,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Nepremýšľam príliš nad tým, prečo sa druhí cítia tak, ako sa cítia.'
    },
    {
        // "súhlasím";3;0
        id: '54',
        factors: SurveyFactorTypes.HEX,
        type: SurveyQuestionTypes.TRUEFALSE,
        weight: 3,
        title: 'Ľudia, ktorí "hrajú" vždy iba férovo, len málokedy dosiahnu to, čo chcú.'
    },
    {
        id: '64',
        factors: SurveyFactorTypes.TEAMWORK,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Tím alebo pracovnú skupinu väčšinou aj tak ťahá len pár silných pracovníkov.'
    },
    {
        // "súhlasím";1;0
        id: 'L74',
        factors: SurveyFactorTypes.LIE,
        type: SurveyQuestionTypes.TRUEFALSE,
        weight: 1,
        title: 'Všetky moje návyky sú v podstate dobré.'
    },
    {
        id: '84',
        factors: SurveyFactorTypes.MORALITY,
        type: SurveyQuestionTypes.CHOICES,
        title: 'Keď narazím na pravidlá, ktoré vnímam, že ma brzdia v dosiahnutí cieľa (výsledku, úspechu, nejakého benefitu a pod.)...',
        options: [
            {
                value: 2,
                text: '...zvažujem čo riskujem tým, že pravidlá obídem.'
            },
            {
                value: 6,
                text: '...dodržím nastavené pravidlá, aj keď je to pre mňa nevýhodné.'
            },
            {
                value: 1,
                text: '...pravidlá neriešim, idem si za svojím cieľom.'
            },
            {
                value: 4,
                text: '...zvažujem aký môže mať dopad na iných to, ak poruším pravidlá.'
            }
        ]
    },
    {
        id: '5',
        factors: SurveyFactorTypes.PERFORMANCE,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Je pre mňa dôležitejšie veci vymyslieť a rozmýšľať o nich, ako ich realizovať.'
    },
    {
        id: '15',
        factors: SurveyFactorTypes.SELFREFLECTION,
        type: SurveyQuestionTypes.SCALE,
        title: 'Keď je niekto naštvaný, premýšľam či to môže súvisieť s niečím, čo som povedal/a alebo urobil/a.'
    },
    {
        id: '25',
        factors: SurveyFactorTypes.CONSCIENTIOUSNESS,
        type: SurveyQuestionTypes.CHOICES,
        title: 'Keď sa učím niečo nové a nejde mi to, napr. práca s novým softwareom, nový šport...',
        options: [
            {
                value: 6,
                text: '...skúšam to dovtedy, kým to neviem poriadne.'
            },
            {
                value: 4,
                text: '...skúšam to aspoň dovtedy, kým sa mi aspoň niečo nepodarí.'
            },
            {
                value: 2,
                text: '...chvíľu to skúšam, ale potom radšej nájdem niečo, čo mi ide lepšie.'
            },
            {
                value: 1,
                text: '...nechám to tak, lebo je dôležité nemrhať energiu na niečo, na čo nemám talent.'
            }
        ]
    },
    {
        id: '35',
        factors: SurveyFactorTypes.DIS,
        type: SurveyQuestionTypes.SCALE,
        title: 'Som často ten/tá, ktorého/ktorú je v spoločnosti vidieť a počuť.'
    },
    {
        id: '45',
        factors: SurveyFactorTypes.EMO,
        type: SurveyQuestionTypes.SCALE,
        title: 'Baví ma pozorovať a premýšľať o pocitoch druhých ľudí.'
    },
    {
        id: '55',
        factors: SurveyFactorTypes.HEX,
        type: SurveyQuestionTypes.SCALE,
        title: 'Myslím, že si zaslúžim viac rešpektu ako väčšina mojich kolegov.'
    },
    {
        id: '65',
        factors: SurveyFactorTypes.TEAMWORK,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Nie som úplne tímový človek.'
    },
    {
        // "súhlasím";1;0
        id: 'L75',
        factors: SurveyFactorTypes.LIE,
        type: SurveyQuestionTypes.TRUEFALSE,
        weight: 1,
        title: 'Nikdy som nezobral/a nič (ani malú vec), čo by patrilo niekomu inému.'
    },
    {
        id: '85',
        factors: SurveyFactorTypes.MORALITY,
        type: SurveyQuestionTypes.SCALE,
        title: 'Som citlivý/á na neetické správanie u iných.'
    },
    {
        id: '6',
        factors: SurveyFactorTypes.PERFORMANCE,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Úspech vo veľkej miere závisí od šťastia.'
    },
    {
        id: '16',
        factors: SurveyFactorTypes.SELFREFLECTION,
        type: SurveyQuestionTypes.CHOICES,
        title: 'Keď mi niekto niečo vytkne...',
        options: [
            {
                value: 2,
                text: '...väčšinou cítim potrebu sa brániť.'
            },
            {
                value: 1,
                text: '...mávam tendenciu ihneď oponovať.'
            },
            {
                value: 4,
                text: '...nesúhlasím, ale zaujíma ma, prečo si to myslí.'
            },
            {
                value: 6,
                text: '...dotkne sa ma to a dlho o tom premýšľam.'
            }
        ]
    },
    {
        id: '26',
        factors: SurveyFactorTypes.CONSCIENTIOUSNESS,
        type: SurveyQuestionTypes.SCALE,
        title: 'Často si všímam drobnosti (napr. chyby v texte), ktoré ostatní ani nevidia.'
    },
    {
        id: '36',
        factors: SurveyFactorTypes.DIS,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Dosť mi vadia ľudia, ktorí sa predvádzajú.'
    },
    {
        id: '46',
        factors: SurveyFactorTypes.EMO,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Často som prekvapený/á, že sa druhí z ničoho nič nahnevajú.'
    },
    {
        id: '56',
        factors: SurveyFactorTypes.HEX,
        type: SurveyQuestionTypes.SCALE,
        title: 'Pri porovnávaní s ostatnými v mojom veku, je pre mňa dôležité byť lepší/ lepšia než väčšina z nich.'
    },
    {
        id: '66',
        factors: SurveyFactorTypes.TEAMWORK,
        type: SurveyQuestionTypes.SCALE,
        title: 'Preferujem tímové hodnotenie a ohodnotenie (i finančné).'
    },
    {
        // "súhlasím";1;0
        id: 'L76',
        factors: SurveyFactorTypes.LIE,
        type: SurveyQuestionTypes.TRUEFALSE,
        weight: 1,
        title: 'Ešte sa mi nestalo, že by som stratil/a alebo poškodil/a niečo, čo patrilo niekomu inému.'
    },
    {
        id: '86',
        factors: SurveyFactorTypes.MORALITY,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'V dnešnej dobe sa nedá byť úplne morálnym človekom.'
    },
    {
        id: '7',
        factors: SurveyFactorTypes.PERFORMANCE,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Najviac mi vyhovujú také úlohy, kde už dopredu viem ako na ne.'
    },
    {
        id: '17',
        factors: SurveyFactorTypes.SELFREFLECTION,
        type: SurveyQuestionTypes.CHOICES,
        title: 'Keď urobím chybu...',
        options: [
            {
                value: 1,
                text: '...je treba hodiť to za hlavu a ísť ďalej.'
            },
            {
                value: 6,
                text: '...treba sa k tomu vrátiť a rozobrať to.'
            },
            {
                value: 2,
                text: '...nemá zmysel sa trápiť, stane sa to každému.'
            },
            {
                value: 4,
                text: '...je potrebné sa ospravedlniť, vysvetliť dôvody.'
            }
        ]
    },
    {
        id: '27',
        factors: SurveyFactorTypes.CONSCIENTIOUSNESS,
        type: SurveyQuestionTypes.SCALE,
        title: 'Mám chvíľami tendenciu byť perfekcionista.'
    },
    {
        id: '37',
        factors: SurveyFactorTypes.DIS,
        type: SurveyQuestionTypes.SCALE,
        title: 'Baví ma byť v centre pozornosti a predvádzať sa pred ostatnými.'
    },
    {
        id: '47',
        factors: SurveyFactorTypes.EMO,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Do práce sa emócie nemajú ťahať.'
    },
    {
        id: '57',
        factors: SurveyFactorTypes.HEX,
        type: SurveyQuestionTypes.SCALE,
        title: 'Pravidlá sú pre obyčajných ľudí, ja sa nimi až tak riadiť nemusím.'
    },
    {
        id: '67',
        factors: SurveyFactorTypes.TEAMWORK,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Ruší ma, keď sa ma pri práci neustále niekto pýta na radu či pomoc.'
    },
    {
        // "súhlasím";1;0
        id: 'L77',
        factors: SurveyFactorTypes.LIE,
        type: SurveyQuestionTypes.TRUEFALSE,
        weight: 1,
        title: 'Nikdy som nepovedal/a nič zlé o niekom druhom.'
    },
    {
        // "súhlasím";3;0
        id: '87',
        factors: SurveyFactorTypes.MORALITY,
        type: SurveyQuestionTypes.TRUEFALSE,
        weight: 3,
        title: 'Viem si spomenúť na situáciu, keď som sa nezachoval/a eticky/morálne.'
    },
    {
        id: '8',
        factors: SurveyFactorTypes.PERFORMANCE,
        type: SurveyQuestionTypes.SCALE,
        title: 'Keď sa pre niečo rozhodnem, idem si za tým hlava, nehlava.'
    },
    {
        id: '18',
        unaccounted: true,
        factors: SurveyFactorTypes.SELFREFLECTION,
        type: SurveyQuestionTypes.CHOICES,
        title: 'Keď sa mi chvíľu niečo nedarí...',
        options: [
            {
                value: 1,
                text: '...mám tendenciu radšej ísť robiť niečo iné.'
            },
            {
                value: 2,
                text: '...skúšam pokus-omyl, až kým sa mi to nepodarí.'
            },
            {
                value: 4,
                text: '...premýšľam, prečo to nefunguje, kde je pes zakopaný.'
            },
            {
                value: 6,
                text: '...nechám si poradiť od niekoho skúsenejšieho alebo hľadám návody, postupy...(napr. na internete).'
            }
        ]
    },
    {
        id: '28',
        factors: SurveyFactorTypes.CONSCIENTIOUSNESS,
        type: SurveyQuestionTypes.SCALE,
        title: 'Kolegovia mi niekedy hovoria, že sa priveľmi zdržiavam detailami.'
    },
    {
        id: '38',
        factors: SurveyFactorTypes.DIS,
        type: SurveyQuestionTypes.SCALE,
        title: 'Nerobí mi problém vyviesť nejakú blbosť - hlavne, že sa ostatní bavia.'
    },
    {
        id: '48',
        factors: SurveyFactorTypes.EMO,
        type: SurveyQuestionTypes.SCALE,
        title: 'Často sa pýtam kolegov na to, ako sa cítia alebo čo prežívajú.'
    },
    {
        id: '58',
        factors: SurveyFactorTypes.HEX,
        type: SurveyQuestionTypes.SCALE,
        title: 'Myslím si, že mám pravdu častejšie než väčšina mojich rovesníkov.'
    },
    {
        id: '68',
        factors: SurveyFactorTypes.TEAMWORK,
        type: SurveyQuestionTypes.SCALE,
        title: 'Úspechy i neúspechy sú vždy zásluhou celého tímu.'
    },
    {
        // "súhlasím";1;0
        id: 'L78',
        factors: SurveyFactorTypes.LIE,
        type: SurveyQuestionTypes.TRUEFALSE,
        weight: 1,
        title: 'Ako dieťa som asi nikdy nebol/a k rodičom drzý/á a neúctivý/á.'
    },
    {
        id: '88',
        factors: SurveyFactorTypes.MORALITY,
        type: SurveyQuestionTypes.CHOICES,
        title: 'Pokiaľ ide činnosť/aktivita môjho okolia proti mojím morálnym hodnotám...',
        options: [
            {
                value: 2,
                text: '...činnosti/aktivity sa nezúčastním, ale ďalej to neriešim, nič ma do toho nie je.'
            },
            {
                value: 1,
                text: '...činnosti/aktivity sa zúčastním, pokiaľ mi vie priniesť benefit.'
            },
            {
                value: 4,
                text: '...činnosti/aktivity sa nezúčastním a nahlas vyslovím svoj nesúhlas s jej morálnym aspektom.'
            },
            {
                value: 6,
                text: '...činnosti/aktivity sa pokúsim zastaviť.'
            }
        ]
    },
    {
        id: '9',
        factors: SurveyFactorTypes.PERFORMANCE,
        type: SurveyQuestionTypes.SCALE,
        title: 'Súťaživosť medzi členmi tímu je zdravá a prospešná.'
    },
    {
        id: '19',
        factors: SurveyFactorTypes.SELFREFLECTION,
        type: SurveyQuestionTypes.SCALE,
        title: 'Ľudia mi hovoria, že o sebe niekedy až veľmi premýšľam.'
    },
    {
        id: '29',
        factors: SurveyFactorTypes.CONSCIENTIOUSNESS,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Som ochotný/á tolerovať i drobné chyby, dôležité je však dokončiť prácu rýchlo a efektívne.'
    },
    {
        id: '39',
        factors: SurveyFactorTypes.DIS,
        type: SurveyQuestionTypes.SCALE,
        title: 'Pravidlá sú na to, aby sa porušovali.'
    },
    {
        id: '49',
        factors: SurveyFactorTypes.EMO,
        type: SurveyQuestionTypes.SCALE,
        title: 'Je pre mňa veľmi dôležité vedieť, že sa ľudia okolo mňa cítia dobre. '
    },
    {
        id: '59',
        factors: SurveyFactorTypes.HEX,
        type: SurveyQuestionTypes.SCALE,
        title: 'Vadia mi ľudia, ktorí si myslia, že sú lepší než ja.'
    },
    {
        id: '69',
        factors: SurveyFactorTypes.TEAMWORK,
        type: SurveyQuestionTypes.SCALE,
        title: 'Prínosy tímovej spolupráce prevyšujú jej nedostatky. '
    },
    {
        // "súhlasím";1;0
        id: 'L79',
        factors: SurveyFactorTypes.LIE,
        type: SurveyQuestionTypes.TRUEFALSE,
        weight: 1,
        title: 'Nikdy som nepodvádzal/a pri žiadnej hre.'
    },
    {
        // "súhlasím";3;0
        id: '89',
        factors: SurveyFactorTypes.MORALITY,
        type: SurveyQuestionTypes.TRUEFALSE,
        weight: 3,
        title: 'Nedá sa len tak posúdiť čo je morálne a nemorálne.'
    },
    {
        id: '10',
        factors: SurveyFactorTypes.PERFORMANCE,
        type: SurveyQuestionTypes.SCALE,
        title: 'Kolegovia mi často hovoria, že som až príliš súťaživý/á.'
    },
    {
        id: '20',
        factors: SurveyFactorTypes.SELFREFLECTION,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Zdá sa mi zvláštne, keď má niekto tendenciu sa v sebe "hrabať".'
    },
    {
        id: '30',
        factors: SurveyFactorTypes.CONSCIENTIOUSNESS,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Skôr sa zameriavam na podstatu problému, detaily neriešim. '
    },
    {
        id: '40',
        factors: SurveyFactorTypes.DIS,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Nemám vo zvyku sa pred ostatnými chváliť. '
    },
    {
        id: '50',
        factors: SurveyFactorTypes.EMO,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Cítim sa nesvoj/a, keď ľudia okolo mňa výrazne prežívajú svoje emócie.'
    },
    {
        id: '60',
        factors: SurveyFactorTypes.HEX,
        type: SurveyQuestionTypes.SCALE,
        title: 'Zvládol/zvládla som zložitejšie a náročnejšie situácie v živote, než väčšina ľudí v mojom veku.'
    },
    {
        id: '70',
        factors: SurveyFactorTypes.TEAMWORK,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'V tímoch sa veľa ľudí často iba "vezie".'
    },
    {
        // "súhlasím";1;0
        id: 'L80',
        factors: SurveyFactorTypes.LIE,
        type: SurveyQuestionTypes.TRUEFALSE,
        weight: 1,
        title: 'Nikdy v živote som nikoho nevyužil/a, ani v malej veci.'
    },
    {
        id: '90',
        factors: SurveyFactorTypes.MORALITY,
        type: SurveyQuestionTypes.SCALE,
        reversed: true,
        title: 'Úspech a morálne hodnoty často nejdú dohromady.'
    },
    // Question no. 7 from Chuť pomôcť Slovensku
    {
        id: 'CHPS7',
        unaccounted: true,
        factors: SurveyFactorTypes.HELP_SLOVAKIA,
        type: SurveyQuestionTypes.SCALE,
        title: 'Moji známi sa zapájajú do dobrovoľníckych alebo pro-bono aktivít.'
    },
    // Question no. 8 from Chuť pomôcť Slovensku
    {
        id: 'CHPS8',
        unaccounted: true,
        factors: SurveyFactorTypes.HELP_SLOVAKIA,
        type: SurveyQuestionTypes.SCALE,
        title: 'Za posledné dva mesiace som pomohol/la niekomu mimo svojej rodiny a priateľov.'
    }
]

export const initializedSelfKnowledgeSurvey = {
    questions: SelfKnowledgeSurveyQuestions,
    surveyType: SurveyTypes.SELF_KNOWLEDGE,
    hash: uuidv4()
} as SelfKnowledgeSurvey
